import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Play, Pause, RotateCcw, AlertTriangle, CheckCircle, TrendingUp, Clock, Zap, Shield } from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

const TaskSimulation = ({ task, steps = [] }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [simulationState, setSimulationState] = useState("ready");
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    let interval;
    if (isPlaying && currentStep < steps.length) {
      interval = setInterval(() => {
        setCurrentStep(prev => {
          if (prev >= steps.length - 1) {
            setIsPlaying(false);
            setSimulationState("complete");
            generateAnalysis();
            return prev;
          }
          return prev + 1;
        });
      }, 1500);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentStep, steps.length]);

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setSimulationState("ready");
    setAnalysis(null);
  };

  const togglePlay = () => {
    if (simulationState === "complete") {
      reset();
    }
    setIsPlaying(!isPlaying);
    if (simulationState === "ready") {
      setSimulationState("running");
    }
  };

  const generateAnalysis = () => {
    // Generate detailed analysis and predictions
    const successRate = 85 + Math.random() * 12;
    const timeEstimate = steps.length * 1.2 + Math.random() * 2;
    const energyCost = steps.length * 0.8 + Math.random() * 1.5;
    const riskScore = Math.random() * 30 + 10;
    
    setAnalysis({
      successRate: successRate.toFixed(1),
      timeEstimate: timeEstimate.toFixed(1),
      energyCost: energyCost.toFixed(2),
      riskScore: riskScore.toFixed(1),
      recommendations: [
        "Increase safety margin by 15% in step 3",
        "Consider parallel execution for steps 2 and 4",
        "Add sensor calibration check before step 1",
        "Implement fallback strategy for step 5"
      ],
      performanceData: steps.map((step, idx) => ({
        step: `Step ${idx + 1}`,
        efficiency: 70 + Math.random() * 25,
        reliability: 80 + Math.random() * 18
      })),
      riskBreakdown: [
        { category: "Human Safety", score: 95, fill: "#10B981" },
        { category: "Equipment", score: 88, fill: "#00D2FF" },
        { category: "Precision", score: 92, fill: "#A855F7" },
        { category: "Time", score: 85, fill: "#F97316" },
        { category: "Energy", score: 90, fill: "#3B82F6" }
      ],
      predictedOutcomes: [
        { outcome: "Complete Success", probability: 75 },
        { outcome: "Partial Success", probability: 20 },
        { outcome: "Needs Retry", probability: 5 }
      ]
    });
  };

  const defaultSteps = [
    { id: 1, name: "Initialize", status: "success", duration: "0.2s" },
    { id: 2, name: "Scan Environment", status: "success", duration: "0.8s" },
    { id: 3, name: "Plan Path", status: "success", duration: "1.2s" },
    { id: 4, name: "Execute Movement", status: "warning", duration: "3.5s", warning: "Human detected - adjusting path" },
    { id: 5, name: "Complete Task", status: "success", duration: "0.5s" }
  ];

  const simSteps = steps.length > 0 ? steps : defaultSteps;

  return (
    <div className="space-y-6">
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Task Execution Simulation</CardTitle>
            <Badge className={
              simulationState === "running" ? "bg-blue-500/10 text-blue-400 border-blue-500/20" :
              simulationState === "complete" ? "bg-green-500/10 text-green-400 border-green-500/20" :
              "bg-gray-500/10 text-gray-400 border-gray-500/20"
            }>
              {simulationState === "running" ? "Running" : simulationState === "complete" ? "Complete" : "Ready"}
            </Badge>
          </div>
          {task && <p className="text-sm text-gray-400 mt-2">{task}</p>}
        </CardHeader>
        <CardContent>
          {/* Robot Animation */}
          <div className="mb-6 h-48 bg-[#050505] rounded-lg border border-white/5 relative overflow-hidden">
            <div
              className="absolute bottom-6 transition-all duration-1000 ease-in-out"
              style={{
                left: `${(currentStep / Math.max(simSteps.length - 1, 1)) * 80 + 10}%`,
                transform: 'translateX(-50%)'
              }}
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center">
                <span className="text-2xl">🤖</span>
              </div>
            </div>

            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <line x1="10" y1="80" x2="90" y2="80" stroke="#333" strokeWidth="2" strokeDasharray="5,5" />
              <line x1="10" y1="80" x2={10 + (currentStep / Math.max(simSteps.length - 1, 1)) * 80} y2="80" stroke="#00D2FF" strokeWidth="3" />
            </svg>

            {simSteps.map((step, idx) => (
              <div
                key={step.id}
                className="absolute bottom-6"
                style={{ left: `${(idx / Math.max(simSteps.length - 1, 1)) * 80 + 10}%`, transform: 'translateX(-50%)' }}
              >
                <div className={`w-3 h-3 rounded-full ${idx <= currentStep ? 'bg-cyan-400' : 'bg-gray-600'}`} />
              </div>
            ))}
          </div>

          {/* Progress */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Progress</span>
              <span className="text-cyan-400 font-mono">{Math.round((currentStep / simSteps.length) * 100)}%</span>
            </div>
            <Progress value={(currentStep / simSteps.length) * 100} className="h-2" />
          </div>

          {/* Steps */}
          <div className="space-y-2 mb-6">
            {simSteps.map((step, idx) => (
              <div
                key={step.id}
                className={`p-3 rounded-lg border transition-all ${
                  idx === currentStep
                    ? 'bg-cyan-500/10 border-cyan-500/30'
                    : idx < currentStep
                    ? 'bg-white/5 border-white/10'
                    : 'bg-transparent border-white/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {idx < currentStep ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : idx === currentStep ? (
                      <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-gray-600" />
                    )}
                    <span className={`font-medium ${idx <= currentStep ? 'text-white' : 'text-gray-500'}`}>
                      {step.name}
                    </span>
                    {step.warning && idx === currentStep && (
                      <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-xs">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {step.warning}
                      </Badge>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">{step.duration}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div className="flex gap-3">
            <Button
              onClick={togglePlay}
              className="flex-1 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20"
            >
              {isPlaying ? (
                <>
                  <Pause className="w-4 h-4 mr-2" />
                  Pause
                </>
              ) : simulationState === "complete" ? (
                <>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Replay
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  {simulationState === "running" ? "Resume" : "Start Simulation"}
                </>
              )}
            </Button>
            <Button
              onClick={reset}
              variant="outline"
              className="border-white/10 hover:bg-white/5 text-white"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Analysis Section (shows after completion) */}
      {analysis && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-[#0A0A0A] border-green-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <p className="text-xs text-gray-400">Success Rate</p>
                </div>
                <p className="text-3xl font-bold text-green-400 font-mono">{analysis.successRate}%</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0A0A0A] border-cyan-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-cyan-400" />
                  <p className="text-xs text-gray-400">Est. Time</p>
                </div>
                <p className="text-3xl font-bold text-cyan-400 font-mono">{analysis.timeEstimate}s</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0A0A0A] border-purple-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-purple-400" />
                  <p className="text-xs text-gray-400">Energy Cost</p>
                </div>
                <p className="text-3xl font-bold text-purple-400 font-mono">{analysis.energyCost} kWh</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0A0A0A] border-orange-500/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-orange-400" />
                  <p className="text-xs text-gray-400">Risk Score</p>
                </div>
                <p className="text-3xl font-bold text-orange-400 font-mono">{analysis.riskScore}%</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Performance Chart */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-base">Step Performance Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analysis.performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="step" stroke="#888" tick={{ fill: '#888', fontSize: 11 }} />
                    <YAxis stroke="#888" tick={{ fill: '#888' }} domain={[0, 100]} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar dataKey="efficiency" fill="#00D2FF" name="Efficiency %" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="reliability" fill="#A855F7" name="Reliability %" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Risk Assessment Radar */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-base">Risk Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <RadarChart data={analysis.riskBreakdown}>
                    <PolarGrid stroke="#333" />
                    <PolarAngleAxis dataKey="category" tick={{ fill: '#888', fontSize: 11 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#888' }} />
                    <Radar name="Safety Score" dataKey="score" stroke="#00FF94" fill="#00FF94" fillOpacity={0.3} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Predictions */}
          <Card className="bg-[#0A0A0A] border-cyan-500/20">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-cyan-400" />
                Predicted Outcomes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {analysis.predictedOutcomes.map((pred, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-300">{pred.outcome}</span>
                    <span className="text-sm font-bold text-cyan-400">{pred.probability}%</span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-2">
                    <div 
                      className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full transition-all duration-1000"
                      style={{ width: `${pred.probability}%` }}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card className="bg-purple-950/20 border-purple-500/20">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-purple-400" />
                Optimization Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {analysis.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-gray-300">
                    <span className="text-purple-400 mt-0.5">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Detailed Analysis */}
          <Card className="bg-gradient-to-br from-[#0A0A0A] to-cyan-950/20 border-cyan-500/20">
            <CardHeader>
              <CardTitle className="text-white">Detailed Analysis</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-gray-300">
              <div>
                <h4 className="font-semibold text-white mb-2">Performance Summary</h4>
                <p>
                  The simulation completed successfully with a predicted success rate of <strong className="text-green-400">{analysis.successRate}%</strong>. 
                  Estimated execution time is <strong className="text-cyan-400">{analysis.timeEstimate} seconds</strong> with an energy consumption of <strong className="text-purple-400">{analysis.energyCost} kWh</strong>.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-white mb-2">Safety Analysis</h4>
                <p>
                  Overall risk score: <strong className="text-orange-400">{analysis.riskScore}%</strong> (Low). 
                  Human safety protocols achieve <strong className="text-green-400">95%</strong> compliance. 
                  Equipment safety is rated at <strong className="text-cyan-400">88%</strong>. 
                  All critical safety thresholds are met.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-white mb-2">Optimization Potential</h4>
                <p>
                  Analysis reveals opportunities to improve efficiency by <strong className="text-purple-400">12-18%</strong> through parallel execution and sensor pre-calibration. 
                  Implementing the recommended safety margin increase will improve reliability to <strong className="text-green-400">97%+</strong>.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-white mb-2">Next Steps</h4>
                <p>
                  Based on simulation results, this task is <strong className="text-green-400">ready for deployment</strong> with minor adjustments. 
                  Consider running additional stress tests under high-load conditions before production rollout.
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default TaskSimulation;
